import React, { useState, useEffect } from 'react';
import { TextField, InputAdornment, Skeleton, Tooltip } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { searchContacts } from '../services/webRTCService';
import styled from '@emotion/styled';
import { validatePhoneNumber } from '@/utils/phone-validation';

// Styled Tooltip component
const StyledTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))`
    & .MuiTooltip-tooltip {
        background-color: #e74c3c;
        color: white;
        font-weight: 500;
    }
    & .MuiTooltip-arrow {
        color: #e74c3c;
    }
`;

const Title = styled.h1`
    font-family: 'Roboto', sans-serif;
    color: #607179;
    font-size: 1rem;
    font-weight: 500;
    line-height: 24px;
    margin-top: 0;
    margin-bottom: 14px;
`;

const ContactsList = styled.div`
    margin-top: 1rem;
`;

const ContactInfo = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    .call {
        width: 24px;
        height: 24px;
        cursor: pointer;
    }
    .content {
        display: flex;
        flex-direction: row;
        gap: 0.75rem;
    }
`;

const PhoneNumber = styled.div`
    display: flex;
    flex-direction: column;
    .nameInfo {
        color: rgb(71 84 103 / 1);
        font-weight: 600;
        font-size: 0.875rem;
        line-height: 1.25rem;
        max-width: 160px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    span {
        color: rgb(71 84 103 / 1);
        font-size: 0.875rem;
        line-height: 1.25rem;
    }
`;

const Divider = styled.hr`
    border-top: 1px solid rgb(234 236 240 / 1);
    margin: 8px 0;
`;

const NotFoundMessage = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    text-align: center;
    color: #607179;

    svg {
        width: 64px;
        height: 64px;
        margin-bottom: 1rem;
    }

    h2 {
        font-size: 1.5rem;
        margin-bottom: 0.5rem;
    }

    p {
        font-size: 1rem;
    }
`;

const SkeletonLoading = () => (
    <div style={{ margin: '10px' }}>
        {[...Array(7)].map((_, index) => (
            <React.Fragment key={index}>
                <ContactInfo>
                    <div className="content">
                        <Skeleton variant="circular" width={40} height={40} />
                        <PhoneNumber>
                            <Skeleton variant="text" width={120} />
                            <Skeleton variant="text" width={80} />
                        </PhoneNumber>
                    </div>
                    <Skeleton variant="circular" width={24} height={24} />
                </ContactInfo>
                <Divider />
            </React.Fragment>
        ))}
    </div>
);

const Contacts = ({
    handleCall,
    auth,
    setContactList,
    contactList,
    selectedBnum,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredContacts, setFilteredContacts] = useState([]);
    const [mycontacts, setMyContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [tooltipAnchorEl, setTooltipAnchorEl] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    useEffect(() => {
        const fetchContacts = async () => {
            setLoading(true);
            try {
                if (contactList.length === 0) {
                    const data = await searchContacts(
                        auth,
                        'recent',
                        '',
                        100,
                        false
                    );
                    console.log('API response for recent contacts:', data);
                    setMyContacts(data.data.contacts);
                    setFilteredContacts(data.data.contacts);
                    setContactList(data.data.contacts);
                    console.log('Fetched recent contacts', data.data.contacts);
                } else if (contactList.length > 0) {
                    setMyContacts(contactList);
                    setFilteredContacts(contactList);
                } else {
                    console.log('No recent contacts found');
                }
            } catch (error) {
                console.error('Error fetching recent contacts:', error);
                setError('Failed to fetch recent contacts');
            } finally {
                setLoading(false);
            }
        };

        fetchContacts();
    }, []);

    useEffect(() => {
        const fetchFilteredContacts = async () => {
            if (searchTerm.length > 0) {
                if (searchTerm.length > 2) {
                    setLoading(true);
                    console.log('Search term: ', searchTerm);
                    try {
                        const data = await searchContacts(
                            auth,
                            'search',
                            searchTerm,
                            100,
                            false
                        );
                        setFilteredContacts(data.data.contacts);
                    } catch (error) {
                        console.error(
                            'Error fetching filtered contacts:',
                            error
                        );
                        // setError('Failed to fetch filtered contacts');
                    } finally {
                        setLoading(false);
                    }
                }
            } else {
                // If search term is empty, show all contacts
                setFilteredContacts(mycontacts);
                setLoading(false);
                console.log('No search term provided');
            }
        };

        const debounceFetch = setTimeout(() => {
            if (!loading) fetchFilteredContacts();
        }, 500); // Adjust the delay as needed (500ms in this case)

        return () => clearTimeout(debounceFetch); // Cleanup the timeout on unmount or when searchTerm changes
    }, [searchTerm]);

    const handleName = (contact) => {
        if (
            contact.firstNameLowerCase == null &&
            contact.lastNameLowerCase == null
        )
            return 'No name';
        if (
            contact.firstNameLowerCase == null &&
            contact.lastNameLowerCase != null
        )
            return `${contact.lastNameLowerCase.charAt(0).toUpperCase() + contact.lastNameLowerCase.slice(1)}`;
        if (
            contact.firstNameLowerCase != null &&
            contact.lastNameLowerCase == null
        )
            return `${contact.firstNameLowerCase.charAt(0).toUpperCase() + contact.firstNameLowerCase.slice(1)}`;
        return `${contact.firstNameLowerCase.charAt(0).toUpperCase() + contact.firstNameLowerCase.slice(1)} ${contact.lastNameLowerCase.charAt(0).toUpperCase() + contact.lastNameLowerCase.slice(1)}`;
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleContactsCall = (contact, event) => {
        if (!selectedBnum) {
            // Set the tooltip anchor to the clicked element
            setTooltipAnchorEl(event.currentTarget);
            setTooltipOpen(true);

            // Auto-hide the tooltip after 3 seconds
            setTimeout(() => {
                setTooltipOpen(false);
            }, 5000);
            return;
        }

        // Validate the contact's phone number
        const phoneNumber = contact.phone?.slice(1); // Remove leading +
        const country = selectedBnum.country?.toLowerCase();

        if (phoneNumber && country) {
            const result = validatePhoneNumber(phoneNumber, country);
            if (!result.isValid) {
                const countryName =
                    selectedBnum?.country?.toUpperCase() ||
                    'the selected country';
                setTooltipAnchorEl(event.currentTarget);
                setTooltipOpen(true);
                setErrorMessage(
                    `Invalid phone number for ${countryName}. Please use a valid ${countryName} number format.`
                );

                // Auto-hide the tooltip after 3 seconds
                setTimeout(() => {
                    setTooltipOpen(false);
                }, 5000);
                return;
            }
        }

        handleCall(handleName(contact), phoneNumber);
    };

    if (loading) return <SkeletonLoading />;
    if (error) return <div>Error: {error}</div>;

    return (
        <div className="flex flex-col px-2 h-full">
            <Title>Contacts</Title>
            <TextField
                fullWidth
                variant="outlined"
                placeholder="Search for Contact (min 3 chars)"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="2"
                                stroke="currentColor"
                                aria-hidden="true"
                                style={{ width: '1rem', height: '1rem' }}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M21 21l-3.5-3.5m2.5-6a8.5 8.5 0 11-17 0 8.5 8.5 0 0117 0z"
                                />
                            </svg>
                        </InputAdornment>
                    ),
                }}
            />
            <ContactsList>
                {filteredContacts && filteredContacts.length > 0 ? (
                    filteredContacts.map((contact, index) => (
                        <React.Fragment key={index}>
                            <ContactInfo>
                                <div className="content">
                                    <AccountCircleIcon
                                        color="primary"
                                        sx={{
                                            width: '40px',
                                            height: '40px',
                                        }}
                                    />
                                    <PhoneNumber>
                                        <div
                                            className="nameInfo"
                                            title={handleName(contact)}
                                        >
                                            {handleName(contact)}
                                        </div>
                                        <span>
                                            {contact.phone
                                                ? contact.phone
                                                : 'No Number'}
                                        </span>
                                    </PhoneNumber>
                                </div>
                                {contact.phone &&
                                (contact.phone.startsWith('+60') ||
                                    contact.phone.startsWith('+61')) &&
                                contact.phone &&
                                ['au', 'my'].includes(
                                    contact?.country?.toLowerCase()
                                ) ? (
                                    <StyledTooltip
                                        open={
                                            tooltipOpen &&
                                            tooltipAnchorEl ===
                                                document.getElementById(
                                                    `call-btn-${index}`
                                                )
                                        }
                                        title="Please select a tracking number first"
                                        placement="top"
                                        arrow
                                    >
                                        <div
                                            id={`call-btn-${index}`}
                                            className="call"
                                            onClick={(e) =>
                                                handleContactsCall(contact, e)
                                            }
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                strokeWidth="2"
                                                stroke="currentColor"
                                                aria-hidden="true"
                                                className="contact-dial h-5 w-5 cursor-pointer text-gray-600"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M8.38 8.853a14.603 14.603 0 002.847 4.01 14.603 14.603 0 004.01 2.847c.124.06.187.09.265.112.28.082.625.023.862-.147.067-.048.124-.105.239-.219.35-.35.524-.524.7-.639a2 2 0 012.18 0c.176.115.35.29.7.64l.195.194c.532.531.797.797.942 1.082a2 2 0 010 1.806c-.145.285-.41.551-.942 1.082l-.157.158c-.53.53-.795.794-1.155.997-.4.224-1.02.386-1.478.384-.413-.001-.695-.081-1.26-.241a19.038 19.038 0 01-8.283-4.874A19.039 19.039 0 013.17 7.761c-.16-.564-.24-.846-.241-1.26a3.377 3.377 0 01.384-1.477c.202-.36.467-.625.997-1.155l.157-.158c.532-.53.798-.797 1.083-.941a2 2 0 011.805 0c.286.144.551.41 1.083.942l.195.194c.35.35.524.525.638.7a2 2 0 010 2.18c-.114.177-.289.352-.638.701-.115.114-.172.172-.22.238-.17.238-.228.582-.147.862.023.08.053.142.113.266z"
                                                ></path>
                                            </svg>
                                        </div>
                                    </StyledTooltip>
                                ) : (
                                    ''
                                )}
                            </ContactInfo>
                            {index < filteredContacts.length - 1 && <Divider />}
                        </React.Fragment>
                    ))
                ) : (
                    <NotFoundMessage>
                        {console.log('No contacts found', filteredContacts)}
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                        <h2>Contact Not Found</h2>
                        <p>
                            We couldn't find any contacts matching your search.
                        </p>
                    </NotFoundMessage>
                )}
            </ContactsList>
        </div>
    );
};

export default Contacts;
